import { ObjectUpdatedType } from "@/enums/ObjectUpdatedType.enum";
import { AppConstants } from "@/helpers/app.constants";
import { DictionaryHelper } from "@/helpers/dictionary.helper";
import { PropertyHelper } from "@/helpers/property.helper";
import { UserHelper } from "@/helpers/user.helper";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { DictionaryCacheService } from "@/services/dictionary-cache.service";
import { HttpErrorResponse } from "@angular/common/http";
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  Attribute,
  AttributeGroup,
  AttributeType,
  ErrorBusinessCode,
  PropertiesService,
  PropertyAttribute,
  RealProperty,
} from "@eemo/api-client";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import {
  NgClass,
  NgFor,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
} from "@angular/common";
import { MatOptionModule } from "@angular/material/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { Subscription } from "rxjs";
import { InputDecimalValueDirective } from "../../extensions/inputDecimalValueDirective";
import { InputNullValueDirective } from "../../extensions/inputNullValueDirective";
import { InputTrimDirective } from "../../extensions/inputTrimDirective";
import { InputUnitDirective } from "../../extensions/inputUnitDirective";

@Component({
  selector: "app-property-attributes-form",
  templateUrl: "./property-attributes-form.component.html",
  styleUrls: ["./property-attributes-form.component.scss"],
  standalone: true,
  imports: [
    MatExpansionModule,
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    NgFor,
    TooltipDirective,
    MatFormFieldModule,
    NgSwitch,
    NgSwitchCase,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    InputDecimalValueDirective,
    InputUnitDirective,
    NgSwitchDefault,
    InputTrimDirective,
    InputNullValueDirective,
    NgClass,
    MatIconModule,
    MatButtonModule,
  ],
})
export class PropertyAttributesFormComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() prop: RealProperty;
  @Input() group: AttributeGroup;
  @Input() values: { [key: string]: PropertyAttribute };
  @Input() openInEditMode = false;
  @ViewChild("fieldContainer") fieldContainerDiv!: ElementRef<HTMLDivElement>;

  groupForm: UntypedFormGroup;
  attributes: Attribute[] = [];
  groupCollapsed: boolean;
  groupEdition = false;

  panelDescription: string;
  fieldClass = "col-12";

  updateSub = new Subscription();
  deleteSub = new Subscription();

  private resizeObserver!: ResizeObserver;

  constructor(
    private dictCacheService: DictionaryCacheService,
    private propService: PropertiesService,
    private alertService: AlertService,
    private appConfigService: AppConfigService,
    public userHelper: UserHelper,
    public dictHelper: DictionaryHelper,
    public propHelper: PropertyHelper
  ) {}

  f(field: string): AbstractControl {
    return this.groupForm.get(field);
  }

  ngOnInit() {
    this.dictCacheService
      .getAttributesForGroup(this.group.code)
      .forEach((attr) => {
        if (this.dictHelper.isAttrAvailableForProperty(attr, this.prop)) {
          this.attributes.push(attr);
        }
      });
    this.panelDescription = this.getPanelDescription();
    this.groupCollapsed = this.appConfigService.isMobileOrTablet;
    this.group.display.collapsed;
    const controls = {};
    this.attributes.forEach((attr) => {
      controls[attr.code] = this.getAttrFormControl(attr);
    });
    this.groupForm = new UntypedFormGroup(controls);
    this.fillFormGroup();
    this.groupEdition = this.openInEditMode;
    if (this.groupEdition) {
      //force required fields in error
      this.groupForm.markAllAsTouched();
    }
  }

  ngAfterViewInit(): void {
    // manage container div responsive size
    // Initialize ResizeObserver
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        //console.log(entry);
        const width = entry.contentRect.width;
        if (width < 450) {
          this.fieldClass = "col-12";
        } else if (width < 750) {
          this.fieldClass = "col-6";
        } else {
          this.fieldClass = "col-4";
        }
      }
    });
    this.resizeObserver.observe(this.fieldContainerDiv.nativeElement);
  }

  ngOnDestroy(): void {
    this.updateSub.unsubscribe();
    this.deleteSub.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  getAttrFormControl(attr: Attribute): AbstractControl {
    const validators: ValidatorFn[] = [];
    if (attr.required) {
      validators.push(Validators.required);
    }
    switch (attr.type) {
      case AttributeType.INTEGER:
        validators.push(Validators.pattern(AppConstants.FORM_PATTERN_INTEGER));
        break;
      case AttributeType.DECIMAL:
        validators.push(Validators.pattern(AppConstants.FORM_PATTERN_DECIMAL));
        break;
    }
    return new UntypedFormControl("", validators);
  }

  editGroup() {
    this.groupEdition = true;
  }
  closeEditGroup() {
    this.groupEdition = false;
    this.fillFormGroup();
  }
  fillFormGroup() {
    if (this.values) {
      const formattedValues: { [key: string]: string } = {};
      Object.keys(this.values).forEach((key) => {
        formattedValues[this.values[key].code] = this.values[key].value;
      });
      this.groupForm.patchValue(formattedValues);
    }
  }

  onSubmit() {
    if (this.groupForm.valid) {
      const attrsToUpdate: PropertyAttribute[] = [];
      const attrsToDelete: string[] = [];
      this.attributes.forEach((attr) => {
        if (this.f(attr.code).value && this.f(attr.code).value !== "NULL") {
          // a value is set : update
          attrsToUpdate.push({
            code: attr.code,
            value: this.f(attr.code).value,
          });
          this.values[attr.code] = {
            code: attr.code,
            value: this.f(attr.code).value,
          };
        } else if (this.values[attr.code]) {
          // a previous value was set and the field is empty : delete
          attrsToDelete.push(attr.code);
          this.values[attr.code] = undefined;
        }
      });
      this.updateSub = this.propService
        .updateRealPropertyAttributes(this.prop.uid, attrsToUpdate)
        .subscribe(() => {
          if (attrsToDelete.length > 0) {
            this.deleteSub = this.propService
              .deleteRealPropertyAttributes(this.prop.uid, attrsToDelete)
              .subscribe({
                next: (r) => {
                  this.updateSucceed();
                },
                error: (err: HttpErrorResponse) => {
                  if (
                    err.error &&
                    err.error.businessCode &&
                    err.error.businessCode ===
                      ErrorBusinessCode.CERTIFIED_ATTRIBUTE
                  ) {
                    attrsToDelete.forEach((i) =>
                      this.groupForm.get(i).setErrors({ certified: true })
                    );
                  }
                },
              });
          } else {
            this.updateSucceed();
          }
        });
    }
  }

  updateSucceed() {
    this.alertService.success("Modification effectuée avec succès");
    this.groupEdition = false;
    this.appConfigService.triggerObjectUpdated(ObjectUpdatedType.ATTRIBUTE);
    this.panelDescription = this.getPanelDescription();
  }

  hasValueDefined() {
    let ret = false;
    this.attributes.forEach((attr) => {
      if (this.values[attr.code]) {
        ret = true;
        return;
      }
    });
    return ret;
  }

  getPropertyFieldValueClass(
    attr: Attribute,
    value: PropertyAttribute
  ): string {
    let fieldClass = "property-field-value";
    if (this.dictHelper.isAttrUpdateSinceCertif(attr, value)) {
      fieldClass += " update-since-certif";
    }
    return fieldClass;
  }

  getPanelDescription() {
    const valuesCount = this.attributes.filter(
      (a) => this.values[a.code] && this.values[a.code].value !== ""
    ).length;
    switch (valuesCount) {
      case 0:
        return "Aucun attribut";
      case 1:
        return "1 attribut";
      default:
        return valuesCount + " attributs";
    }
  }
}
